import React, { useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import bowser from 'yola-bowser';
import { designSystem } from '@yola/ws-ui';
import { utils } from '@yola/ws-sdk';
import ModalWithPanels from 'src/js/modules/common/components/modal-with-panels';
import fieldNames from '../constants/field-names';
import translateExistingPageOptionsValues from '../constants/translate-existing-page-options-values';

const {
  ControlGroup,
  LanguageSelectField,
  SearchableSelectField,
  CheckboxField,
  Stack,
  Icon,
  Tag,
  Box,
  RadioButtonGroupField,
  HorizontalStack,
} = designSystem;

const DIALOG_WIDTH = 368;
const FLOATING_ELEMENT_SELECTOR = '.ws-ui-base-select-option, .ws-ui-base-select__option';

const defaultFieldsValidation = {
  page: utils.noop,
  langCode: utils.noop,
};

function TranslatePageDialog({
  onSubmit,
  onCancel,
  pagesOptions,
  languageOptions,
  captions,
  fieldsValidation = defaultFieldsValidation,
  roadmapUrl,
  control,
  showRadioButtonGroup,
  selectedPageId,
}) {
  const selectRef = useRef();
  const isMobile = bowser.mobile;

  const adaptiveLanguageOptions = useMemo(
    () =>
      languageOptions.map((group) => ({
        ...group,
        options: group.options.map((item) => ({
          value: item.code,
          iconComponent: item.iconComponent,
          label: item.name,
        })),
      })),
    [languageOptions]
  );

  const adaptivePageOptions = useMemo(
    () =>
      pagesOptions.map((item) => ({
        value: item.id,
        label: item.name,
      })),
    [pagesOptions]
  );

  const handleFocus = () => {
    selectRef?.current?.openMenuAutocomplete();
  };

  const translateExistingPageOptions = useMemo(
    () => [
      {
        label: captions.radioButtonGroupCaptions.radioButton1,
        value: translateExistingPageOptionsValues.OVERRIDE_PAGE,
        isLabelBold: true,
      },
      {
        label: captions.radioButtonGroupCaptions.radioButton2,
        value: translateExistingPageOptionsValues.COPY_PAGE,
        isLabelBold: true,
      },
    ],
    [captions.radioButtonGroupCaptions]
  );

  return (
    <ModalWithPanels
      width={DIALOG_WIDTH}
      height="auto"
      centered
      onCancel={onCancel}
      onOverlayClick={onCancel}
      onSubmit={onSubmit}
      onPressEsc={onCancel}
      captions={{
        title: captions.title,
        submitButton: captions.submit,
        cancelButton: captions.cancel,
      }}
      overlay="visible"
      className="ws-translate-page-dialog"
      floatingModalElementSelector={FLOATING_ELEMENT_SELECTOR}
    >
      <Stack gap="spacing-xs">
        <ControlGroup title={`${captions.fieldLabels.page}:`}>
          <SearchableSelectField
            ref={selectRef}
            key={selectedPageId}
            control={control}
            name={fieldNames.TRANSLATE_PAGE_ID}
            options={adaptivePageOptions}
            placeholder={captions.selectPlaceholder}
            noResultsText={captions.noResultsText}
            rules={{
              validate: fieldsValidation.page,
            }}
            openMenuOnFocus
            preventOverflow
            {...(isMobile && { onFocus: handleFocus })}
          />
        </ControlGroup>
        <ControlGroup title={`${captions.fieldLabels.language}:`}>
          <LanguageSelectField
            control={control}
            name={fieldNames.TRANSLATE_LANGUAGE_SELECT}
            options={adaptiveLanguageOptions}
            rules={{
              validate: fieldsValidation.langCode,
            }}
            preventOverflow
            appearance="searchable"
          />
        </ControlGroup>
        {showRadioButtonGroup && (
          <ControlGroup
            title={captions.radioButtonGroupCaptions.title}
            description={captions.radioButtonGroupCaptions.description}
          >
            <RadioButtonGroupField
              name={fieldNames.EXISTING_PAGE_RESOLVING}
              control={control}
              options={translateExistingPageOptions}
            />
          </ControlGroup>
        )}
      </Stack>
      {roadmapUrl && (
        <Box paddingTop="spacing-m">
          <HorizontalStack gap="spacing-3-xs">
            <CheckboxField
              id="autoTransId"
              name={fieldNames.TRANSLATE_AUTO_TRANS}
              control={control}
              label={
                <span className="ws-translate-page-dialog__checbox-label">
                  {captions.fieldLabels.checkbox}&nbsp;
                  <Icon glyph="ai" type="accent" />
                </span>
              }
            />
            <Tag size="small" appearance="cta-secondary" label="PRO" />
          </HorizontalStack>
        </Box>
      )}
    </ModalWithPanels>
  );
}

TranslatePageDialog.propTypes = {
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onCheckboxChange: PropTypes.func,
  roadmapUrl: PropTypes.string.isRequired,
  initialValues: PropTypes.shape({
    pageId: PropTypes.string,
    langCode: PropTypes.string,
    autoTrans: PropTypes.bool,
  }),
  fieldsValidation: PropTypes.shape({
    page: PropTypes.func,
    langCode: PropTypes.func,
  }),
  languageOptions: PropTypes.arrayOf(PropTypes.shape().isRequired).isRequired,
  pagesOptions: PropTypes.arrayOf(PropTypes.shape().isRequired).isRequired,
  captions: PropTypes.shape({
    title: PropTypes.string,
    fieldLabels: PropTypes.shape({
      page: PropTypes.string,
      language: PropTypes.string,
      checkbox: PropTypes.string,
    }),
    selectPlaceholder: PropTypes.string,
    noResultsText: PropTypes.string,
    submit: PropTypes.string,
    cancel: PropTypes.string,
    radioButtonGroupCaptions: PropTypes.shape({
      title: PropTypes.string,
      description: PropTypes.string,
      radioButton1: PropTypes.string,
      radioButton2: PropTypes.string,
    }),
  }).isRequired,
  control: PropTypes.shape({}).isRequired,
  showRadioButtonGroup: PropTypes.bool.isRequired,
  selectedPageId: PropTypes.string.isRequired,
};

export default TranslatePageDialog;
